import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { trackPageView } from "@/services/analytics";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Index from "./pages/Index";
import Privacy from "./pages/Privacy";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import SampleReports from "./pages/SampleReports";
import SampleReportDetail from "./pages/SampleReportDetail";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

// Layout component that conditionally shows Header/Footer
const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  
  // Track page views and scroll to top on route changes
  useEffect(() => {
    // Scroll to top of page
    window.scrollTo(0, 0);
    
    // Track page view
    const pageName = getPageName(location.pathname);
    trackPageView(pageName, window.location.href);
  }, [location]);
  
  // Helper to get readable page names
  const getPageName = (path: string): string => {
    const pageNames: Record<string, string> = {
      '/': 'Home',
      '/about': 'About',
      '/privacy': 'Privacy Policy',
      '/faq': 'FAQ',
      '/sample-reports': 'Sample Reports',
      '/blog': 'Blog',
      '/admin': 'Admin Dashboard',
    };
    
    // Handle dynamic pages
    if (path.startsWith('/sample-reports/')) {
      return 'Sample Report Detail';
    }
    if (path.startsWith('/blog/')) {
      return 'Blog Post';
    }
    
    return pageNames[path] || 'Unknown Page';
  };
  
  // Hide header/footer on home page for cleaner search experience
  const isHomePage = location.pathname === "/";
  
  return (
    <div className="flex flex-col min-h-screen">
      {!isHomePage && <Header />}
      <main className={isHomePage ? "" : "flex-1"}>
        {children}
      </main>
      {!isHomePage && <Footer />}
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/sample-reports" element={<SampleReports />} />
            <Route path="/sample-reports/:slug" element={<SampleReportDetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin" element={<Admin />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
